import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useImagePreloader } from '../hooks/useImagePreloader';
import { getImagesToPreload } from '../config/imagePreloadConfig';
import '../styles/buildingplan.css';
import FeedbackModal from './FeedbackModal';
import { getPreviousStep } from '../utils/navigationUtils';

interface ProgressStep {
  id: number;
  title: string;
  progressPercent: string;
  completed: boolean;
  progressWidth?: string;
}

interface TestimonialData {
  id: number;
  name: string;
  avatar: string;
  text: string;
}

const testimonialsData: TestimonialData[] = [
  {
    id: 1,
    name: "Jessica, 32 y.o.",
    avatar: "J",
    text: "“I didn’t realize how much tension I carried until my face looked fresher and more relaxed. Now I finally recognize myself in the mirror again.”"
  },
  {
    id: 2,
    name: "Laura, 46 y.o.",
    avatar: "L",
    text: "“Years of desk work slowly changed my posture. With Age Back, my body feels open and light again. I move more freely and feel stronger in everyday life.”"
  },
  {
    id: 3,
    name: "Helen, 58 y.o.",
    avatar: "H",
    text: "“I thought slowing down was just part of getting older. Age Back helped me restore mobility and daily energy. I wake up feeling lighter, active, and confident again.”"
  }
];

const BuildingPlanPage: React.FC = () => {
  const { stepId } = useParams<{ stepId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const currentStepId = stepId ? parseInt(stepId) : 1;

  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    {
      id: 1,
      title: "Aligning your rewind goals...",
      progressPercent: "0%",
      completed: false,
      progressWidth: "0%"
    },
    {
      id: 2,
      title: "Reading your body signals...",
      progressPercent: "0%",
      completed: false,
      progressWidth: "0%"
    },
    {
      id: 3,
      title: "Reviewing lifestyle patterns...",
      progressPercent: "0%",
      completed: false,
      progressWidth: "0%"
    },
    {
      id: 4,
      title: "Building your rewind plan...",
      progressPercent: "0%",
      completed: false,
      progressWidth: "0%"
    }
  ]);

  const [currentProgress, setCurrentProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showSecondModal, setShowSecondModal] = useState(false);
  const [activeTestimonialId, setActiveTestimonialId] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isPausedRef = useRef(false);
  const continueLoadingRef = useRef<(() => void) | null>(null);
  const continueSecondLoadingRef = useRef<(() => void) | null>(null);
  const testimonialTimerRef = useRef<NodeJS.Timeout | null>(null);

  const imagesToPreload = [
    
    '/image/arrow_left.svg',
    '/image/app-logo.svg',
    '/image/rating.svg',
    '/image/chart-12-weeks.webp'
  ];
  useImagePreloader(imagesToPreload);

  const animateProgressBar = (stepId: number, targetPercent: number, duration: number = 2500): Promise<void> => {
    return new Promise((resolve) => {
      const startTime = Date.now();

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const currentPercent = Math.round(targetPercent * progress);

        setProgressSteps(prev => prev.map(step =>
          step.id === stepId ? {
            ...step,
            progressPercent: `${currentPercent}%`,
            progressWidth: `${currentPercent}%`,
            completed: progress >= 1 && targetPercent === 100
          } : step
        ));

        if (progress < 1) {
          requestAnimationFrame(updateProgress);
        } else {

          setProgressSteps(prev => prev.map(step =>
            step.id === stepId ? {
              ...step,
              progressPercent: `${targetPercent}%`,
              progressWidth: `${targetPercent}%`,
              completed: targetPercent === 100
            } : step
          ));
          resolve();
        }
      };

      requestAnimationFrame(updateProgress);
    });
  };

  useEffect(() => {

    const loadProgressBars = async () => {

      await animateProgressBar(1, 100, 2500);

      setShowModal(true);
      isPausedRef.current = true;

      const continueLoading = () => {

        const loadRemainingSteps = async () => {

          await animateProgressBar(2, 100, 2500);

          await animateProgressBar(3, 100, 2500);

          setShowSecondModal(true);
          isPausedRef.current = true;

          const continueFinalLoading = () => {

            const loadFinalStep = async () => {
              await animateProgressBar(4, 100, 2500);

              navigate('/ready-plan');
            };
            loadFinalStep();
          };

          continueSecondLoadingRef.current = continueFinalLoading;
        };
        loadRemainingSteps();
      };

      continueLoadingRef.current = continueLoading;
    };

    loadProgressBars();
  }, []);

  useEffect(() => {
    const startTestimonialRotation = () => {
      testimonialTimerRef.current = setInterval(() => {
        setIsTransitioning(true);

        setTimeout(() => {
          setActiveTestimonialId(prev => {
            const nextId = prev >= testimonialsData.length ? 1 : prev + 1;
            return nextId;
          });

          setTimeout(() => {
            setIsTransitioning(false);
          }, 150);
        }, 150);
      }, 3000); 
    };

    startTestimonialRotation();

    return () => {
      if (testimonialTimerRef.current) {
        clearInterval(testimonialTimerRef.current);
      }
    };
  }, []);

  const handleBackClick = () => {
    const previousStep = getPreviousStep(location.pathname);
    if (previousStep) {
      navigate(previousStep);
    } else {
      navigate('/create-plan');
    }
  };

  const handleModalYes = () => {
    setShowModal(false);
    isPausedRef.current = false;
    if (continueLoadingRef.current) {
      continueLoadingRef.current();
    }
  };

  const handleModalNo = () => {
    setShowModal(false);
    isPausedRef.current = false;
    if (continueLoadingRef.current) {
      continueLoadingRef.current();
    }
  };

  const handleSecondModalYes = () => {
    setShowSecondModal(false);
    isPausedRef.current = false;
    if (continueSecondLoadingRef.current) {
      continueSecondLoadingRef.current();
    }
  };

  const handleSecondModalNo = () => {
    setShowSecondModal(false);
    isPausedRef.current = false;
    if (continueSecondLoadingRef.current) {
      continueSecondLoadingRef.current();
    }
  };

  const handleDotClick = (testimonialId: number) => {
    if (testimonialId !== activeTestimonialId && !isTransitioning) {
      
      if (testimonialTimerRef.current) {
        clearInterval(testimonialTimerRef.current);
      }

      setIsTransitioning(true);

      setTimeout(() => {
        setActiveTestimonialId(testimonialId);

        setTimeout(() => {
          setIsTransitioning(false);

          testimonialTimerRef.current = setInterval(() => {
            setIsTransitioning(true);

            setTimeout(() => {
              setActiveTestimonialId(prev => {
                const nextId = prev >= testimonialsData.length ? 1 : prev + 1;
                return nextId;
              });

              setTimeout(() => {
                setIsTransitioning(false);
              }, 150);
            }, 150);
          }, 3000);
        }, 150);
      }, 150);
    }
  };

  const activeTestimonial = testimonialsData.find(t => t.id === activeTestimonialId) || testimonialsData[0];

  return (
    <div className="quiz-container building-plan-container">
      <div className="top-bar">
        <div className="navbar">
          <button className="back-button goal-back-button" aria-label="Назад" onClick={handleBackClick}>
            <img
              src="/image/arrow_left.svg"
              alt="Back"
              width="6"
              height="12"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('width', '6');
                svg.setAttribute('height', '12');
                svg.setAttribute('viewBox', '0 0 6 12');
                svg.innerHTML = '<path d="M5 11L1 6L5 1" stroke="#28194B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
                e.currentTarget.parentNode?.appendChild(svg);
              }}
            />
          </button>
          <div className="app-icon">
            <img src="/image/app-logo.svg" alt="Age Back" className="app-name" />
          </div>
        </div>
      </div>

      <main className="content-wrapper">
        <div className="building-plan-content">

          <div className="steps-section">

            <div className="building-plan-title-wrapper">
              <div className="heading-container">
                <h3 className="building-plan-title">Building your personal Age Rewind profile…</h3>
              </div>
            </div>

            <div className="steps-container">
              {progressSteps.map((step, index) => {
                const isInactive = step.progressPercent === '0%';

                let stepClasses = 'progress-step';
                if (isInactive) {
                  stepClasses += ' inactive';
                }

                return (
                  <div key={step.id} className={stepClasses}>
                    <div className="step-header">
                      <div className="step-title">{step.title}</div>
                      <div className="step-progress-percent">{step.progressPercent}</div>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className={`progress-bar-background progress-step-${step.id}`}
                        style={{
                          width: step.progressPercent
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="testimonial-section">
            <h2 className="testimonial-title">Trusted by over 500K+ users</h2>

            <div className="testimonial-dots">
              {testimonialsData.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className={`dot ${activeTestimonialId === testimonial.id ? 'active' : ''}`}
                  onClick={() => handleDotClick(testimonial.id)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View testimonial from ${testimonial.name}`}
                />
              ))}
            </div>

            <div className="buildingplan-testimonial-card">
              <div className={`testimonial-content-advanced ${isTransitioning ? 'transitioning' : ''}`}>
                <div className="testimonial-header">
                  <div className="user-info">
                    <div className="user-avatar"><span>{activeTestimonial.avatar}</span></div>
                    <div className="user-details"><span>{activeTestimonial.name}</span></div>
                  </div>
                  <div className="rating">
                    <img src="/image/rating.svg" alt="Rating" />
                  </div>
                </div>
                <div className="testimonial-text">
                  <p>{activeTestimonial.text}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <FeedbackModal
        isVisible={showModal}
        onYes={handleModalYes}
        onNo={handleModalNo}
      />

      <FeedbackModal
        isVisible={showSecondModal}
        onYes={handleSecondModalYes}
        onNo={handleSecondModalNo}
        isSecondModal={true}
      />
    </div>
  );
};

export default BuildingPlanPage;